import React, { useState } from "react";
import { Box, Card, CardContent, Typography, Divider } from "@mui/material";
// Iconsax — ClusterDen's standard icon library (see [[icon-library-iconsax]]); swapped out
// the MUI icon-font set used here before (Rahim, 2026-08-25: "use better icon use from our
// new library").
import { Menu, ArrowDown2, ArrowUp2 } from "iconsax-react";

// Same fixed-pixel width cap TemplateMessageDisplay uses (a percentage width fights the
// outer pill bubble's own shrink-to-fit sizing and leaves an oversized gap around the card).
// Bumped up from 300 (Rahim, 2026-08-25: "the box is small... make it a little big").
const listCardWidthSx = {
  maxWidth: {
    xs: "min(calc((var(--chatbox-width, 100vw)) * 0.6), calc(100vw - 72px))",
    sm: "min(340px, calc(var(--chatbox-width, 100vw) - 96px))",
  },
  width: { xs: "100%", sm: "auto" },
  boxSizing: "border-box",
};

// WhatsApp interactive list message. Extracted out of ChatBody.jsx (2026-08-25) so it can
// render through ChatCard's shared pill-bubble scaffold, same as every other message type —
// status/time used to be drawn inside this card itself (a second, independent copy of the
// same tick-icon logic every other bubble has); that's dropped here since ChatCard's own
// date/tick row now covers it.
//
// Visual pass (2026-08-25): brought this in line with the template cards — fixed-light
// "document" colors, left-aligned text instead of the old center-everything layout, a real
// pixel width cap, and the WhatsApp-green button/section styling pulled back to the same
// muted teal accent (#1F7A5C) the templates use (Rahim: "dont emphasis on using green
// colour keep it simple yet elegant with proper padding"). Second pass: the header and the
// section title were both bold/near-same size and read as duplicate headings — the header is
// now the one true heading (bigger, bolder) and the section title dropped to a small muted
// uppercase eyebrow, same hierarchy convention as the catalog card (Rahim: "all the headings
// use the same font bold which is wrong").
function ListMessageCard({ data }) {
  const [expanded, setExpanded] = useState(false);

  // Malformed/degraded payload — a real WhatsApp list always ships a button + at least one
  // section; when both are missing there's nothing to expand, so `body` is all that renders
  // and looked identical to normal message copy. Italic + muted grey (Rahim: "use grey
  // colour", was amber) marks it as a fallback note, not real content.
  const isFallbackBody = !data.button && !(data.sections && data.sections.length > 0);

  return (
    <Card
      sx={{
        ...listCardWidthSx,
        borderRadius: "14px",
        overflow: "hidden",
        border: "1px solid #DADCDE",
        backgroundColor: "#FFFFFF",
        padding: 0,
        boxShadow: "none",
      }}
    >
      {/* MUI bakes a `&:last-child { padding-bottom: 24px }` rule into CardContent that
          survives `padding: 0` — see TemplateMessageDisplay for the same fix. */}
      <CardContent sx={{ padding: 0, "&:last-child": { paddingBottom: 0 } }}>
        {/* Header — matches the exact heading treatment TemplateMessageDisplay uses for its
            own HEADER TEXT (fontWeight 600 / 0.9375rem), same "document title" font
            everywhere (Rahim, 2026-08-25: "the heading font being used in the order
            confirmed template use the same font"). */}
        {data.header && (
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.9375rem",
              color: "#050505",
              padding: "14px 14px 6px 14px",
            }}
          >
            {data.header}
          </Typography>
        )}

        {/* Body */}
        <Typography
          component="div"
          sx={{
            padding: data.header ? "0 14px 8px 14px" : "14px 14px 8px 14px",
            fontSize: "0.875rem",
            fontWeight: 400,
            fontStyle: isFallbackBody ? "italic" : "normal",
            lineHeight: 1.45,
            color: isFallbackBody ? "#8A8D91" : data.header ? "#65676B" : "#050505",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {data.body}
        </Typography>

        {/* Footer */}
        {data.footer && (
          <Typography
            sx={{ display: "block", fontSize: "0.75rem", fontWeight: 400, color: "#8A8D91", padding: "0 14px 10px 14px" }}
          >
            {data.footer}
          </Typography>
        )}

        {/* Button — real WhatsApp template style: full-width row, divider above, no chrome.
            Root cause of "button not visible in dark mode" (Rahim, 2026-08-25): `all: "unset"`
            plus a later `color` in the *same* sx object silently drops the color declaration
            entirely when emotion serializes it (confirmed live — the compiled rule's `color`
            stayed `unset`, so the native <button> just inherited the surrounding dark-theme
            text color instead of our teal, no matter what hex we picked). Fixed by resetting
            only the specific native-button defaults we need gone, instead of the `all`
            shorthand, so `color` is a normal standalone declaration again. */}
        {data.button && (
          <>
            <Divider sx={{ borderColor: "#E6E7EB" }} />
            <Typography
              component="button"
              onClick={() => setExpanded((prev) => !prev)}
              sx={{
                border: "none",
                outline: "none",
                background: "none",
                margin: 0,
                font: "inherit",
                textAlign: "inherit",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                boxSizing: "border-box",
                padding: "11px 14px",
                color: "#1F7A5C",
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: "pointer",
                transition: "background-color 0.15s ease",
                "&:hover": { backgroundColor: "#1F7A5C14" },
              }}
            >
              <Box sx={{ display: "flex", mr: 1 }}>
                <Menu size="18" color="currentColor" variant="Linear" />
              </Box>
              {data.button}
              <Box sx={{ display: "flex", ml: 0.5 }}>
                {expanded ? (
                  <ArrowUp2 size="16" color="currentColor" variant="Linear" />
                ) : (
                  <ArrowDown2 size="16" color="currentColor" variant="Linear" />
                )}
              </Box>
            </Typography>
          </>
        )}

        {/* Sections/Options */}
        {expanded && data.sections && data.sections.length > 0 && (
          <Box>
            {data.sections.map((section, i) => (
              <Box key={i}>
                <Divider sx={{ borderColor: "#E6E7EB" }} />
                {section.title && (
                  <Typography
                    sx={{
                      color: "#8A8D91",
                      fontWeight: 600,
                      fontSize: "0.6875rem",
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      padding: "10px 14px 4px 14px",
                    }}
                  >
                    {section.title}
                  </Typography>
                )}
                {section.rows &&
                  section.rows.map((row, j) => (
                    <Box
                      key={j}
                      sx={{
                        padding: "9px 14px",
                        cursor: "pointer",
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: "#F5F6F7" },
                      }}
                    >
                      <Typography sx={{ fontWeight: 400, color: "#050505", fontSize: "0.8125rem" }}>
                        {row.title}
                      </Typography>
                      {row.description && (
                        <Typography sx={{ fontWeight: 400, color: "#65676B", fontSize: "0.75rem" }}>
                          {row.description}
                        </Typography>
                      )}
                    </Box>
                  ))}
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default ListMessageCard;
